import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Clock, Users, Monitor, ArrowUpRight, Building2, HelpCircle } from "lucide-react";

const courses = [
  {
    id: "adult-two-day",
    tag: "Flagship",
    title: "Adult MHFA — Two Day",
    length: "2 days · Online or Classroom",
    format: "Live instructor-led",
    price: "£325 pp",
    priceLabel: "Standard course fee",
    priceNote: "Discounts available for group open course bookings and in-house training.",
    excerpt:
      "The full Mental Health First Aider certification. Gain the skills to spot the signs of someone experiencing poor mental health, confidently start a conversation, and connect to appropriate support.",
    icon: Users,
    accent: "#4A5D52",
    highlight: true,
    ctaType: "dual",
    inhouseCourseKey: "Adult MHFA — Two Day (in-house / private group)",
  },
  {
    id: "refresher",
    tag: "Refresh & Renew",
    title: "MHFA Refresher",
    length: "Half day",
    format: "For existing MHFAiders",
    price: "£150 pp",
    priceLabel: "Standard course fee",
    priceNote: "Discounts available for group open course bookings and in-house training.",
    excerpt:
      "For anyone who has completed a 1 or 2-day MHFA course. Update your knowledge, refresh skills and share experiences with peers.",
    icon: Clock,
    accent: "#D88C76",
    ctaType: "dual",
    inhouseCourseKey: "MHFA Refresher (in-house / private group)",
  },
  {
    id: "aware",
    tag: "Awareness",
    title: "MHFA Aware",
    length: "Half day",
    format: "Delivered online or in-person",
    price: "By enquiry",
    priceLabel: "Group pricing",
    priceNote: "",
    excerpt:
      "An introductory awareness session — delivered as private in-house training for your organisation. Ideal for giving every employee foundational mental health literacy.",
    icon: Monitor,
    accent: "#8DA399",
    ctaType: "enquire",
    inhouseCourseKey: "MHFA Aware (in-house)",
  },
];

export default function CourseListings() {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToUpcoming = () => {
    if (location.pathname === "/") {
      const el = document.getElementById("upcoming-courses");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#upcoming-courses");
    }
  };

  const goToInhouse = (course) => {
    navigate(`/contact?course=${encodeURIComponent(course.inhouseCourseKey)}`);
  };

  return (
    <section data-testid="courses-section" id="courses" className="section relative">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7">
            <p className="eyebrow mb-4">Our Courses</p>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#1E2A25]">
              Three MHFA courses. <br />
              <span className="italic text-[#4A5D52]">One shared goal —</span> healthier workplaces.
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="text-[#4A5D52] leading-relaxed">
              Every course is accredited by MHFA England. Book a spot on an upcoming open course, or arrange
              <strong className="text-[#1E2A25] font-medium"> private in-house training</strong> — online or in
              person — for your team.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {courses.map((c, i) => {
            const Icon = c.icon;
            const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-12"];
            return (
              <motion.article
                key={c.id}
                data-testid={`course-card-${c.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`${spans[i]} group relative overflow-hidden rounded-3xl border border-[#8DA399]/30 p-8 md:p-10 bg-[#FDFBF7] hover:-translate-y-1 transition-transform duration-500`}
                style={{
                  backgroundImage: c.highlight
                    ? "linear-gradient(135deg, #F3EFEA 0%, #FDFBF7 60%)"
                    : "none",
                }}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <span
                      className="w-11 h-11 rounded-full flex items-center justify-center"
                      style={{ background: `${c.accent}15`, color: c.accent }}
                    >
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </span>
                    <span className="eyebrow" style={{ color: c.accent }}>{c.tag}</span>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-[#4A5D52] group-hover:rotate-45 transition-transform duration-500" />
                </div>

                <h3 className="font-serif-display text-3xl md:text-4xl text-[#1E2A25] mb-3">{c.title}</h3>
                <p className="text-[#4A5D52] leading-relaxed max-w-xl">{c.excerpt}</p>

                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#4A5D52]">
                  <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> {c.length}</span>
                  <span className="w-1 h-1 rounded-full bg-[#8DA399]" />
                  <span>{c.format}</span>
                </div>

                <div className="mt-6 pt-6 border-t border-[#8DA399]/25">
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#4A5D52]">{c.priceLabel}</span>
                    <span className="text-[#1E2A25] font-medium text-lg">{c.price}</span>
                  </div>
                  {c.priceNote && (
                    <p className="mt-2 text-xs text-[#4A5D52]/85 italic max-w-md leading-relaxed">
                      {c.priceNote}
                    </p>
                  )}
                </div>

                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                  {c.ctaType === "dual" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => goToInhouse(c)}
                        data-testid={`course-inhouse-${c.id}`}
                        className="link-underline bg-transparent border-0 p-0 cursor-pointer"
                      >
                        <Building2 className="w-4 h-4" />
                        In-house training for your team
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={scrollToUpcoming}
                        data-testid={`course-cta-${c.id}`}
                        className="link-underline bg-transparent border-0 p-0 cursor-pointer text-[#4A5D52]"
                      >
                        Upcoming open courses <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => goToInhouse(c)}
                      data-testid={`course-cta-${c.id}`}
                      className="link-underline bg-transparent border-0 p-0 cursor-pointer"
                    >
                      Enquire with us <ArrowUpRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* "Not sure what you need?" callout */}
        <motion.div
          data-testid="unsure-callout"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-10 md:mt-14 rounded-3xl border border-dashed border-[#4A5D52]/35 bg-[#F3EFEA]/60 p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
        >
          <span className="w-12 h-12 rounded-full bg-[#D88C76]/15 text-[#D88C76] flex items-center justify-center flex-shrink-0">
            <HelpCircle className="w-5 h-5" strokeWidth={1.5} />
          </span>
          <div className="flex-1">
            <p className="eyebrow text-[#D88C76] mb-2">Not sure what you need?</p>
            <p className="font-serif-display text-2xl md:text-3xl text-[#1E2A25] leading-snug">
              We can help you figure out the best approach to providing MHFA support in your workplace.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="unsure-cta"
            className="btn-primary flex-shrink-0"
          >
            Enquire with us <ArrowUpRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
